import React, { useState, useRef, useEffect } from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { Capacitor } from '@capacitor/core'
import { performNativeOAuth } from '@/services/authDeepLinkService'
import { logError, logWarn } from '@/lib/log'

interface SocialButtonsProps {
  redirectTo?: string
  emailHint?: string
}

/** Auth flow'un takılması durumunda loading state'i sıfırlayan timeout (ms) */
const AUTH_TIMEOUT_MS = 30_000

export function SocialButtons({ redirectTo, emailHint }: SocialButtonsProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  // Component unmount cleanup
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  /** Loading state'i güvenli şekilde başlat + timeout koy */
  const startLoading = (provider: string) => {
    setLoading(provider)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        logWarn(`[SocialButtons] ${provider} login ${AUTH_TIMEOUT_MS / 1000}s timeout — sıfırlanıyor`)
        setLoading(null)
      }
    }, AUTH_TIMEOUT_MS)
  }

  /** Loading state'i güvenli şekilde sıfırla */
  const stopLoading = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (mountedRef.current) setLoading(null)
  }

  const handleGoogleLogin = async () => {
    try {
      startLoading('google')

      if (Capacitor.isNativePlatform()) {
        const { error } = await performNativeOAuth('google', emailHint)
        if (error) throw error
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectTo || `${window.location.origin}`,
            queryParams: {
              prompt: 'select_account',
              ...(emailHint && { login_hint: emailHint }),
            },
          },
        })
        if (error) throw error
      }
    } catch (error) {
      logError('Google login error:', error)
      alert('Google ile giriş yapılırken bir hata oluştu.')
    } finally {
      stopLoading()
    }
  }

  const handleAppleLogin = async () => {
    try {
      startLoading('apple')

      if (Capacitor.isNativePlatform()) {
        const { error } = await performNativeOAuth('apple')
        if (error) throw error
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo: redirectTo || `${window.location.origin}`,
          },
        })
        if (error) throw error
      }
    } catch (error) {
      logError('Apple login error:', error)
      alert('Apple ile giriş yapılırken bir hata oluştu.')
    } finally {
      stopLoading()
    }
  }

  return (
    <div className="flex flex-col w-full gap-3">
      <LazyMotion features={domAnimation}>
        <m.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleAppleLogin}
          disabled={loading !== null}
          className="w-full h-13 flex items-center justify-center gap-3 bg-black border border-black rounded-xl hover:bg-gray-900 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {loading === 'apple' ? (
            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.19 2.31-.88 3.5-.84 1.5.15 2.53.76 3.2 1.83-2.91 1.74-2.4 5.92.54 7.15-.69 1.57-1.51 3.12-2.32 4.03zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          )}
          <span className="text-[15px] font-bold text-white tracking-wide">
            {loading === 'apple' ? 'Bağlanıyor…' : 'Apple ile Devam Et'}
          </span>
        </m.button>

        <m.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleGoogleLogin}
          disabled={loading !== null}
          className="w-full h-13 flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {loading === 'google' ? (
            <span className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94L5.84 14.1z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
          )}
          <span className="text-[15px] font-bold text-gray-700 tracking-wide">
            {loading === 'google' ? 'Bağlanıyor…' : 'Google ile Devam Et'}
          </span>
        </m.button>
      </LazyMotion>
    </div>
  )
}
